<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { dataHojeBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idPrestacaoContas: 0,
      dataPrestacao: dataHojeBR(),
      observacaoAuditoria: '',
      itens: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const confirmationModalRef = ref(null)

const authStore = useAuthStore()

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const isLoadingListas = ref(false)
const partnerInfo = ref(null)

const accordionState = reactive({
  dados: true,
  creditos: false,
  titulos: false,
  associacao: false
})

const formatarValor = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
}

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))
const isReadOnly = computed(() => {
  if (isEditing.value && item.value.lstStatusPrestacao !== 'FIN_ST_PREST1') return true
  if (isEditing.value && !temPermissaoCompleta.value) return true
  return false
})

const configBuscaParceiro = computed(() => ({
  ...parceiroSearch,
  titulo: 'Buscar Parceiro de Negócios',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

// ============================================================
// CRÉDITOS DISPONÍVEIS
// ============================================================
const creditosDisponiveis = ref([])
const creditosLoading = ref(false)
const creditosSelecionados = ref([])

const carregarCreditos = async () => {
  if (!partnerInfo.value?.idParceiro) return
  creditosLoading.value = true
  try {
    const res = await api.get('/PrestacoesContas/creditos-disponiveis', {
      params: { idParceiro: partnerInfo.value.idParceiro }
    })
    creditosDisponiveis.value = res.data || []
    creditosSelecionados.value = []
  } catch (e) {
    console.error('Erro ao carregar créditos:', e)
    emit('show-notification', { message: 'Erro ao carregar créditos disponíveis.', type: 'error' })
  } finally {
    creditosLoading.value = false
  }
}

// ============================================================
// TÍTULOS EM ABERTO
// ============================================================
const titulosAberto = ref([])
const titulosLoading = ref(false)
const titulosSelecionados = ref([])

const carregarTitulos = async () => {
  if (!partnerInfo.value?.idParceiro) return
  titulosLoading.value = true
  try {
    const res = await api.get('/PrestacoesContas/titulos-abater', {
      params: { idParceiro: partnerInfo.value.idParceiro }
    })
    titulosAberto.value = res.data || []
    titulosSelecionados.value = []
  } catch (e) {
    console.error('Erro ao carregar títulos:', e)
    emit('show-notification', { message: 'Erro ao carregar títulos em aberto.', type: 'error' })
  } finally {
    titulosLoading.value = false
  }
}

// ============================================================
// ASSOCIAÇÃO — Saldos em Conta Corrente
// ============================================================
const associacoes = ref([])

const adicionarAssociacao = () => {
  associacoes.value.push({
    idTituloFinanceiroAdt: null,
    idTituloFinanceiroAbatido: null,
    valorMoedaTitulo: 0,
    valorMoedaBanco: 0,
    valorMoedaPadrao: 0,
    valorMoedaGerencial: null,
    descricaoComplementar: ''
  })
}

const totalAbatido = computed(() => {
  return associacoes.value.reduce((acc, a) => acc + (Number(a.valorMoedaTitulo) || 0), 0)
})

/** Calcula o consumo acumulado de um ADT até a linha anterior ao índice dado */
const consumoAdtAte = (idAdt, ateIndex) => {
  return associacoes.value
    .filter((a, idx) => idx < ateIndex && a.idTituloFinanceiroAdt === idAdt)
    .reduce((acc, a) => acc + (Number(a.valorMoedaTitulo) || 0), 0)
}

/** Calcula o consumo acumulado de um título até a linha anterior ao índice dado */
const consumoTituloAte = (idTitulo, ateIndex) => {
  return associacoes.value
    .filter((a, idx) => idx < ateIndex && a.idTituloFinanceiroAbatido === idTitulo)
    .reduce((acc, a) => acc + (Number(a.valorMoedaTitulo) || 0), 0)
}

/** Retorna o saldo remanescente de um ADT para uma determinada linha */
const saldoAdtParaLinha = (idAdt, linhaIndex) => {
  if (!idAdt) return 0
  const credito = creditosDisponiveis.value.find(c => c.idTituloFinanceiroAdt === idAdt)
  if (!credito) return 0
  const consumido = consumoAdtAte(idAdt, linhaIndex)
  return Math.max(0, Number(credito.saldoDisponivel) - consumido)
}

/** Retorna o saldo remanescente de um título para uma determinada linha */
const saldoTituloParaLinha = (idTitulo, linhaIndex) => {
  if (!idTitulo) return 0
  const titulo = titulosAberto.value.find(t => t.idTituloFinanceiro === idTitulo)
  if (!titulo) return 0
  const consumido = consumoTituloAte(idTitulo, linhaIndex)
  return Math.max(0, Number(titulo.saldoDevedor) - consumido)
}

/** Calcula o valor sugerido = min(saldoRemanescenteADT, saldoRemanescenteTitulo) */
const calcularValorSugerido = (idAdt, idTitulo, linhaIndex) => {
  if (!idAdt || !idTitulo) return 0
  const saldoAdt = saldoAdtParaLinha(idAdt, linhaIndex)
  const saldoTit = saldoTituloParaLinha(idTitulo, linhaIndex)
  return Math.min(saldoAdt, saldoTit)
}

/** Recalcula valores das linhas abaixo de um índice que compartilham ADT ou Título */
const recalcularLinhasAbaixo = (index) => {
  const removida = associacoes.value[index]
  if (!removida) return

  // Percorre linhas abaixo e re-sugere o valor se compartilhar ADT ou Título com a removida
  for (let i = index + 1; i < associacoes.value.length; i++) {
    const assoc = associacoes.value[i]
    const compartilhaAdt = assoc.idTituloFinanceiroAdt === removida.idTituloFinanceiroAdt
    const compartilhaTit = assoc.idTituloFinanceiroAbatido === removida.idTituloFinanceiroAbatido
    if (compartilhaAdt || compartilhaTit) {
      if (assoc.idTituloFinanceiroAdt && assoc.idTituloFinanceiroAbatido) {
        const novoValor = calcularValorSugerido(assoc.idTituloFinanceiroAdt, assoc.idTituloFinanceiroAbatido, i)
        if (novoValor > 0) {
          assoc.valorMoedaTitulo = novoValor
        } else {
          // Se não há mais saldo, zera e marca para atenção
          assoc.valorMoedaTitulo = 0
        }
      }
    }
  }
}

const removerAssociacao = (index) => {
  associacoes.value.splice(index, 1)
  // Recalcula linhas abaixo que compartilhavam ADT ou Título com a removida
  recalcularLinhasAbaixo(index)
}

/** Watch profundo: quando ADT e Título são selecionados, preenche o valor automaticamente */
watch(associacoes, (novaLista) => {
  novaLista.forEach((assoc, idx) => {
    if (assoc.idTituloFinanceiroAdt && assoc.idTituloFinanceiroAbatido) {
      const valorSugerido = calcularValorSugerido(assoc.idTituloFinanceiroAdt, assoc.idTituloFinanceiroAbatido, idx)
      // Só preenche automaticamente se o valor ainda estiver em 0 (não modificado pelo usuário)
      if (Number(assoc.valorMoedaTitulo) === 0 && valorSugerido > 0) {
        assoc.valorMoedaTitulo = valorSugerido
      }
    }
  })
}, { deep: true })

// ============================================================
// SALVAR
// ============================================================
const salvar = async () => {
  if (isReadOnly.value) return

  if (associacoes.value.length === 0) {
    emit('show-notification', { message: 'Adicione pelo menos uma associação de crédito para dívida.', type: 'warning' })
    return
  }

  const payload = {
    ...item.value,
    itens: associacoes.value.map(a => ({
      idTituloFinanceiroAdt: a.idTituloFinanceiroAdt,
      idTituloFinanceiroAbatido: a.idTituloFinanceiroAbatido,
      valorMoedaTitulo: Number(a.valorMoedaTitulo) || 0,
      valorMoedaBanco: Number(a.valorMoedaBanco) || 0,
      valorMoedaPadrao: Number(a.valorMoedaPadrao) || 0,
      valorMoedaGerencial: a.valorMoedaGerencial ? Number(a.valorMoedaGerencial) : null,
      descricaoComplementar: a.descricaoComplementar || null
    }))
  }

  const sucesso = await submitForm('PrestacoesContas', 'idPrestacaoContas', payload)
  if (sucesso && confirmationModalRef.value) {
    confirmationModalRef.value.show()
  }
}

const aprovarPrestacao = async () => {
  const id = item.value?.idPrestacaoContas
  if (!id) return

  isLoading.value = true
  try {
    await api.post(`/PrestacoesContas/${id}/Aprovar`)
    emit('show-notification', { message: 'Prestação de Contas aprovada com sucesso.', type: 'success' })
    emit('itemAtualizado', { ...item.value, lstStatusPrestacao: 'FIN_ST_PREST2' })
  } catch (error) {
    emit('show-notification', { message: error.response?.data?.message || 'Erro ao aprovar prestação.', type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ============================================================
// WATCH: Carrega dados do parceiro quando recebe da tela de créditos
// ============================================================
const inicializarParceiro = (dados) => {
  const id = dados?.idParceiro || dados?.IdParceiro
  const nome = dados?.nomeParceiro || dados?.NomeParceiro
  if (!id) return false

  partnerInfo.value = {
    idParceiro: id,
    nomeParceiro: nome
  }
  carregarCreditos()
  carregarTitulos()
  return true
}

const inicializarAssociacoesDaEdicao = () => {
  const itens = item.value?.itens || item.value?.Itens
  if (!itens || !Array.isArray(itens) || itens.length === 0) return

  associacoes.value = itens.map(i => ({
    idTituloFinanceiroAdt: i.idTituloFinanceiroAdt ?? i.IdTituloFinanceiroAdt ?? null,
    idTituloFinanceiroAbatido: i.idTituloFinanceiroAbatido ?? i.IdTituloFinanceiroAbatido ?? null,
    valorMoedaTitulo: i.valorMoedaTitulo ?? i.ValorMoedaTitulo ?? 0,
    valorMoedaBanco: i.valorMoedaBanco ?? i.ValorMoedaBanco ?? 0,
    valorMoedaPadrao: i.valorMoedaPadrao ?? i.ValorMoedaPadrao ?? 0,
    valorMoedaGerencial: i.valorMoedaGerencial ?? i.ValorMoedaGerencial ?? null,
    descricaoComplementar: i.descricaoComplementar ?? i.DescricaoComplementar ?? ''
  }))
}

watch(() => props.itemParaEditar, (newVal) => {
  // Fluxo de criação: vem do CreditoFornecedorAdministrar com isNovaPrestacao
  if (newVal?.isNovaPrestacao && newVal.idParceiro) {
    inicializarParceiro(newVal)
    return
  }

  // Fluxo de edição: item já foi populado pelo useFormulario via initializeForm
  // Aguarda o item.value ser preenchido (nextTick)
  if (!newVal?.isNovaPrestacao && newVal?.idPrestacaoContas) {
    // O useFormulario já copiou newVal para item.value via initializeForm
    // Precisamos esperar o nextTick para item.value estar populado
    setTimeout(() => {
      if (inicializarParceiro(item.value)) {
        inicializarAssociacoesDaEdicao()
      }
    }, 0)
  }
}, { immediate: true })

onMounted(() => {
  // Fluxo de criação
  if (item.value?.isNovaPrestacao && item.value?.idParceiro) {
    inicializarParceiro(item.value)
    return
  }

  // Fluxo de edição
  if (!item.value?.isNovaPrestacao && item.value?.idPrestacaoContas) {
    if (inicializarParceiro(item.value)) {
      inicializarAssociacoesDaEdicao()
    }
  }
})
</script>

<template>
  <FormularioCadastro
    module-name="Prestação de Contas"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @cancelar="handleCancel"
    :desabilitar-salvar="isReadOnly"
  >
    <!-- MENSAGENS -->
    <div v-if="isReadOnly && item.lstStatusPrestacao && item.lstStatusPrestacao !== 'FIN_ST_PREST1'" class="alert alert-info border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-info-circle-fill fs-4 me-3"></i>
      <div><strong>Prestação Processada:</strong> Esta prestação não pode ser editada.</div>
    </div>

    <!-- FRAME 1: DADOS DA PRESTAÇÃO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.dados = !accordionState.dados">
        <h5 class="mb-0"><i class="bi bi-calendar-check me-2 text-primary"></i> Dados da Prestação</h5>
        <i :class="['bi', accordionState.dados ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.dados">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBusca
              id="parceiro"
              label="Fornecedor *"
              :model-value="partnerInfo?.idParceiro || null"
              :configuracao-busca="configBuscaParceiro"
              :disabled="isReadOnly || !!partnerInfo?.idParceiro"
              @selecionado="val => { partnerInfo = { idParceiro: val.idParceiro, nomeParceiro: val.nome }; carregarCreditos(); carregarTitulos(); }"
              required
            />
          </div>
          <div class="col-md-3">
            <CampoInput id="dataPrestacao" label="Data da Prestação" type="date" v-model="item.dataPrestacao" :required="true" :disabled="isReadOnly" />
          </div>
          <div class="col-md-5">
            <label class="form-label">Observação de Auditoria</label>
            <textarea id="observacaoAuditoria" class="form-control" rows="2" v-model="item.observacaoAuditoria" placeholder="Observações..." :disabled="isReadOnly"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 2: CRÉDITOS DISPONÍVEIS -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.creditos = !accordionState.creditos">
        <h5 class="mb-0"><i class="bi bi-piggy-bank me-2 text-primary"></i> Créditos Disponíveis (ADTs pagos)</h5>
        <i :class="['bi', accordionState.creditos ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.creditos">
        <div v-if="creditosLoading" class="text-center p-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>
        <div v-else-if="creditosDisponiveis.length === 0" class="text-center text-muted py-3">
          <i class="bi bi-inbox me-2"></i> Nenhum crédito disponível para este fornecedor.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;">#</th>
                <th>Nº Fatura</th>
                <th>Valor Total</th>
                <th>Saldo Disponível</th>
                <th>Moeda</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cred in creditosDisponiveis" :key="cred.idTituloFinanceiroAdt">
                <td><span class="fw-bold text-body-secondary">#{{ cred.idTituloFinanceiroAdt }}</span></td>
                <td>{{ cred.numeroFaturaOriginal || '-' }}</td>
                <td class="text-end">{{ formatarValor(cred.valorTotal) }}</td>
                <td class="text-end fw-bold text-success">{{ formatarValor(cred.saldoDisponivel) }}</td>
                <td>{{ cred.lstMoeda }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FRAME 3: TÍTULOS EM ABERTO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.titulos = !accordionState.titulos">
        <h5 class="mb-0"><i class="bi bi-receipt me-2 text-primary"></i> Títulos em Aberto (a abater)</h5>
        <i :class="['bi', accordionState.titulos ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.titulos">
        <div v-if="titulosLoading" class="text-center p-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>
        <div v-else-if="titulosAberto.length === 0" class="text-center text-muted py-3">
          <i class="bi bi-inbox me-2"></i> Nenhum título em aberto para este fornecedor.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;">#</th>
                <th>Nº Fatura</th>
                <th>Valor Total</th>
                <th>Saldo Devedor</th>
                <th>Vencimento</th>
                <th>Moeda</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tit in titulosAberto" :key="tit.idTituloFinanceiro">
                <td><span class="fw-bold text-body-secondary">#{{ tit.idTituloFinanceiro }}</span></td>
                <td>{{ tit.numeroFaturaOriginal || '-' }}</td>
                <td class="text-end">{{ formatarValor(tit.valorTotal) }}</td>
                <td class="text-end fw-bold text-primary">{{ formatarValor(tit.saldoDevedor) }}</td>
                <td class="text-center">{{ tit.dataVencimento ? new Date(tit.dataVencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-' }}</td>
                <td>{{ tit.lstMoeda }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FRAME 4: ASSOCIAÇÃO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-link-45deg me-2 text-primary"></i> Associação: Crédito → Dívida</h5>
        <button v-if="!isReadOnly" type="button" class="btn btn-sm btn-outline-primary" @click="adicionarAssociacao">
          <i class="bi bi-plus-lg me-1"></i>Adicionar
        </button>
      </div>
      <div class="card-body p-0">
        <div v-if="associacoes.length === 0" class="text-center text-muted py-4">
          <i class="bi bi-info-circle me-2"></i>Clique em "Adicionar" para associar um crédito a uma dívida.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>ADT (Crédito)</th>
                <th>Título (Dívida)</th>
                <th class="text-end">Valor (Moeda Título)</th>
                <th>Descrição</th>
                <th v-if="!isReadOnly" style="width: 60px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(assoc, index) in associacoes" :key="index">
                <td>
                  <select class="form-select form-select-sm" v-model="assoc.idTituloFinanceiroAdt" :disabled="isReadOnly">
                    <option :value="null">Selecione...</option>
                    <option v-for="cred in creditosDisponiveis" :key="cred.idTituloFinanceiroAdt" :value="cred.idTituloFinanceiroAdt" :disabled="saldoAdtParaLinha(cred.idTituloFinanceiroAdt, index) <= 0">
                      #{{ cred.idTituloFinanceiroAdt }} - {{ cred.numeroFaturaOriginal || '' }} (R$ {{ formatarValor(saldoAdtParaLinha(cred.idTituloFinanceiroAdt, index)) }})
                    </option>
                  </select>
                </td>
                <td>
                  <select class="form-select form-select-sm" v-model="assoc.idTituloFinanceiroAbatido" :disabled="isReadOnly">
                    <option :value="null">Selecione...</option>
                    <option v-for="tit in titulosAberto" :key="tit.idTituloFinanceiro" :value="tit.idTituloFinanceiro" :disabled="saldoTituloParaLinha(tit.idTituloFinanceiro, index) <= 0">
                      #{{ tit.idTituloFinanceiro }} - {{ tit.numeroFaturaOriginal || '' }} (R$ {{ formatarValor(saldoTituloParaLinha(tit.idTituloFinanceiro, index)) }})
                    </option>
                  </select>
                </td>
                <td>
                  <input type="number" class="form-control form-control-sm text-end" step="0.01" min="0" v-model.number="assoc.valorMoedaTitulo" :disabled="isReadOnly" />
                </td>
                <td>
                  <input type="text" class="form-control form-control-sm" v-model="assoc.descricaoComplementar" :disabled="isReadOnly" placeholder="Obs..." />
                </td>
                <td v-if="!isReadOnly">
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="removerAssociacao(index)">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" class="text-end fw-bold">Total a Abater:</td>
                <td class="text-end fw-bold fs-5">{{ formatarValor(totalAbatido) }}</td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
    <!-- MODAL DE CONFIRMAÇÃO PARA APROVAÇÃO -->
    <ConfirmationModal
      ref="confirmationModalRef"
      title="Aprovar Prestação de Contas"
      message="Deseja aprovar esta prestação de contas agora?<br><small class='text-muted'>Os títulos serão abatidos e não poderão ser editados.</small>"
      confirm-button-text="Sim, Aprovar"
      cancel-button-text="Apenas Salvar"
      @confirmed="(confirmado) => { if (confirmado) aprovarPrestacao() }"
    />
  </FormularioCadastro>
</template>
